import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Download, ExternalLink, FileText, Loader2, Paperclip, RefreshCw, Trash2, Upload, X } from 'lucide-react';
import type { EditableAttachment, PendingAttachment } from '../types/attachmentsForm';
import {
  createPendingAttachment,
  getPendingDisplayName,
  getPendingDisplaySize,
} from '../types/attachmentsForm';
import { deleteAttachment, stageAttachment } from '../utils/attachmentApi';
import { fetchAuthenticatedBlob, openAuthenticatedAttachmentInNewTab } from '../utils/authenticatedDownload';
import {
  canDeleteAttachment,
  canEditAttachmentMetadata,
  canUploadAttachments,
} from '../utils/attachmentPermissions';
import {
  DOCUMENT_ATTACHMENT_ACCEPT,
  formatAttachmentByteSize,
  validateDocumentAttachment,
} from '../utils/attachmentValidation';
import type { IntendedAttachableType } from '../types/attachment';

const isAbortError = (err: unknown) =>
  (err instanceof DOMException && err.name === 'AbortError') ||
  (err instanceof Error && err.name === 'AbortError');

export interface AttachmentsSectionProps {
  title?: string;
  description?: string;
  showIncludeOnSend?: boolean;
  parentIsDraft?: boolean;
  pending: PendingAttachment[];
  onPendingChange: (pending: PendingAttachment[]) => void;
  existing: EditableAttachment[];
  onExistingChange: (existing: EditableAttachment[]) => void;
  disabled?: boolean;
  error?: string | null;
  /** Upload files to staging immediately on pick (QuickBooks-style). */
  stageOnPick?: boolean;
  intendedAttachableType?: IntendedAttachableType;
  uploadSessionId?: string;
}

const AttachmentsSection = ({
  title = 'Attachments',
  description = 'Optional files (PDF, images, DOC, XLSX, CSV, XML). Max 20 MB each. Saved when you save the form.',
  showIncludeOnSend = false,
  parentIsDraft = true,
  pending,
  onPendingChange,
  existing,
  onExistingChange,
  disabled = false,
  error,
  stageOnPick = false,
  intendedAttachableType,
  uploadSessionId,
}: AttachmentsSectionProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [openingId, setOpeningId] = useState<number | null>(null);
  const [removingClientId, setRemovingClientId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const uploadControllersRef = useRef(new Map<string, AbortController>());

  const canUpload = canUploadAttachments() && !disabled;
  const canEditMeta = canEditAttachmentMetadata() && !disabled;
  const canDelete = canDeleteAttachment(parentIsDraft) && !disabled;
  const stagingActive = stageOnPick && !!intendedAttachableType;

  /** Sync pendingRef immediately so concurrent upload completions cannot clobber each other. */
  const applyPendingUpdate = (updater: (prev: PendingAttachment[]) => PendingAttachment[]) => {
    const next = updater(pendingRef.current);
    pendingRef.current = next;
    onPendingChange(next);
  };

  useEffect(() => {
    return () => {
      for (const controller of uploadControllersRef.current.values()) {
        controller.abort();
      }
      uploadControllersRef.current.clear();
    };
  }, []);

  const uploadToStaging = async (clientId: string, file: File, note: string) => {
    uploadControllersRef.current.get(clientId)?.abort();
    const controller = new AbortController();
    uploadControllersRef.current.set(clientId, controller);

    try {
      const attachment = await stageAttachment(file, {
        note,
        intended_attachable_type: intendedAttachableType,
        upload_session_id: uploadSessionId,
        signal: controller.signal,
      });

      // Cancelled after the server finished — clean up the orphaned staged file.
      if (
        controller.signal.aborted ||
        !pendingRef.current.some((row) => row.clientId === clientId)
      ) {
        void deleteAttachment(attachment.id).catch(() => {});
        return;
      }

      applyPendingUpdate((prev) =>
        prev.map((row) =>
          row.clientId === clientId
            ? {
                ...row,
                file: null,
                attachment,
                uploadStatus: 'staged' as const,
                uploadError: undefined,
                note: attachment.note ?? note,
              }
            : row
        )
      );
    } catch (err) {
      if (isAbortError(err) || controller.signal.aborted) return;
      if (!pendingRef.current.some((row) => row.clientId === clientId)) return;

      applyPendingUpdate((prev) =>
        prev.map((row) =>
          row.clientId === clientId
            ? {
                ...row,
                uploadStatus: 'failed' as const,
                uploadError: err instanceof Error ? err.message : 'Upload failed',
              }
            : row
        )
      );
    } finally {
      if (uploadControllersRef.current.get(clientId) === controller) {
        uploadControllersRef.current.delete(clientId);
      }
    }
  };

  const extractFilesFromDataTransfer = (dataTransfer: DataTransfer): File[] => {
    if (dataTransfer.files?.length) {
      return Array.from(dataTransfer.files);
    }

    const fromItems: File[] = [];
    for (const item of Array.from(dataTransfer.items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) fromItems.push(file);
      }
    }
    return fromItems;
  };

  const handleFilesSelected = (files: FileList | File[] | null) => {
    if (!files?.length) return;

    const errors: string[] = [];
    const toUpload: { clientId: string; file: File; note: string }[] = [];
    const newRows: PendingAttachment[] = [];

    for (const file of Array.from(files)) {
      const validation = validateDocumentAttachment(file);
      if (!validation.valid) {
        errors.push(validation.message);
        continue;
      }
      const row = createPendingAttachment(file);
      newRows.push(row);
      if (stagingActive) {
        toUpload.push({ clientId: row.clientId, file, note: row.note });
      }
    }

    if (errors.length > 0) {
      setPickError(errors.join(' '));
    } else {
      setPickError(null);
    }

    if (newRows.length === 0 && errors.length === 0) {
      setPickError('No files were added. Try PDF, image, DOC, DOCX, XLSX, CSV, or XML.');
      return;
    }

    if (newRows.length > 0) {
      // Commit rows to pendingRef before starting uploads so fast responses see the clientIds.
      applyPendingUpdate((prev) => [...prev, ...newRows]);
      for (const item of toUpload) {
        void uploadToStaging(item.clientId, item.file, item.note);
      }
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    if (!canUpload) return;
    handleFilesSelected(extractFilesFromDataTransfer(e.dataTransfer));
  };

  const updatePending = (clientId: string, patch: Partial<PendingAttachment>) => {
    applyPendingUpdate((prev) =>
      prev.map((row) => (row.clientId === clientId ? { ...row, ...patch } : row))
    );
  };

  const removePending = async (clientId: string) => {
    const row = pendingRef.current.find((item) => item.clientId === clientId);
    if (!row) return;

    if (row.uploadStatus === 'uploading') {
      uploadControllersRef.current.get(clientId)?.abort();
      uploadControllersRef.current.delete(clientId);
      applyPendingUpdate((prev) => prev.filter((item) => item.clientId !== clientId));
      return;
    }

    if (stagingActive && row.uploadStatus === 'staged' && row.attachment?.id) {
      setRemovingClientId(clientId);
      try {
        await deleteAttachment(row.attachment.id);
      } catch (err) {
        setPickError(
          `Could not remove ${row.attachment.filename}: ${
            err instanceof Error ? err.message : 'failed'
          }`
        );
        setRemovingClientId(null);
        return;
      }
      setRemovingClientId(null);
    }

    applyPendingUpdate((prev) => prev.filter((item) => item.clientId !== clientId));
  };

  const retryPendingUpload = (row: PendingAttachment) => {
    if (!row.file || !stagingActive) return;
    applyPendingUpdate((prev) =>
      prev.map((item) =>
        item.clientId === row.clientId
          ? { ...item, uploadStatus: 'uploading' as const, uploadError: undefined }
          : item
      )
    );
    void uploadToStaging(row.clientId, row.file, row.note);
  };

  const updateExisting = (id: number, patch: Partial<EditableAttachment>) => {
    onExistingChange(
      existing.map((row) => (row.attachment.id === id ? { ...row, ...patch } : row))
    );
  };

  const handleDownload = async (downloadUrl: string, filename: string, attachmentId: number) => {
    try {
      setDownloadingId(attachmentId);
      const blob = await fetchAuthenticatedBlob(downloadUrl);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setPickError(`Could not download ${filename}.`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOpen = async (downloadUrl: string, filename: string, attachmentId: number) => {
    try {
      setOpeningId(attachmentId);
      await openAuthenticatedAttachmentInNewTab(downloadUrl);
    } catch (err) {
      setPickError(
        err instanceof Error ? err.message : `Could not open ${filename}.`
      );
    } finally {
      setOpeningId(null);
    }
  };

  const attachmentBusy = (attachmentId: number) =>
    downloadingId === attachmentId || openingId === attachmentId;

  const visibleExisting = existing.filter((row) => !row.markedForDelete);

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-gray-500" />
          {title}
        </h3>
        {description && <p className="mt-1 text-xs text-gray-500 leading-relaxed">{description}</p>}
      </div>

      {canUpload && (
        <>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={DOCUMENT_ATTACHMENT_ACCEPT}
            className="hidden"
            disabled={disabled}
            onChange={(e) => handleFilesSelected(e.target.files)}
          />
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              isDragOver
                ? 'border-slate-600 bg-slate-50'
                : 'border-gray-300 bg-gray-50/50 hover:border-gray-400'
            }`}
          >
            <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragOver ? 'text-slate-700' : 'text-gray-400'}`} />
            <p className="text-sm font-medium text-gray-800">
              {isDragOver ? 'Drop files here' : 'Drag and drop files here'}
            </p>
            <p className="text-xs text-gray-500 mt-1">or</p>
            <button
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              className="mt-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
            >
              Choose files
            </button>
          </div>
        </>
      )}

      {(pickError || error) && (
        <p className="text-sm text-red-600">{error || pickError}</p>
      )}

      {pending.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {stagingActive ? 'Staged files' : 'New files'}
          </p>
          {pending.map((row) => (
            <div
              key={row.clientId}
              className={`rounded-lg border border-dashed p-3 space-y-2 ${
                row.uploadStatus === 'failed'
                  ? 'border-red-300 bg-red-50'
                  : 'border-slate-300 bg-slate-50'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                {row.uploadStatus === 'staged' && row.attachment?.download_url ? (
                  <button
                    type="button"
                    onClick={() =>
                      handleOpen(
                        row.attachment!.download_url,
                        row.attachment!.filename,
                        row.attachment!.id
                      )
                    }
                    disabled={attachmentBusy(row.attachment.id)}
                    className="flex items-start gap-2 min-w-0 flex-1 text-left rounded-md hover:bg-white/80 disabled:opacity-60 transition-colors"
                    title="Open in new tab"
                  >
                    <FileText className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p
                        className="text-sm font-medium text-gray-900 truncate hover:underline"
                        title={getPendingDisplayName(row)}
                      >
                        {getPendingDisplayName(row)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatAttachmentByteSize(getPendingDisplaySize(row))}
                        {stagingActive && ' · Ready to link on save'}
                      </p>
                    </div>
                  </button>
                ) : (
                  <div className="flex items-start gap-2 min-w-0">
                    {row.uploadStatus === 'uploading' ? (
                      <Loader2 className="h-4 w-4 text-slate-500 mt-0.5 shrink-0 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p
                        className="text-sm font-medium text-gray-900 truncate"
                        title={getPendingDisplayName(row)}
                      >
                        {getPendingDisplayName(row)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatAttachmentByteSize(getPendingDisplaySize(row))}
                        {row.uploadStatus === 'uploading' && ' · Uploading…'}
                        {row.uploadStatus === 'failed' && row.uploadError && ` · ${row.uploadError}`}
                      </p>
                    </div>
                  </div>
                )}
                {canUpload && (
                  <div className="flex items-center gap-1 shrink-0">
                    {row.uploadStatus === 'failed' && row.file && (
                      <button
                        type="button"
                        onClick={() => retryPendingUpload(row)}
                        className="p-1 text-gray-400 hover:text-slate-700"
                        title="Retry upload"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    )}
                    {row.uploadStatus === 'staged' && row.attachment?.download_url && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            handleOpen(
                              row.attachment!.download_url,
                              row.attachment!.filename,
                              row.attachment!.id
                            )
                          }
                          disabled={attachmentBusy(row.attachment.id)}
                          className="p-1.5 text-gray-500 hover:text-slate-800 hover:bg-gray-100 rounded-md"
                          title="Open in new tab"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleDownload(
                              row.attachment!.download_url,
                              row.attachment!.filename,
                              row.attachment!.id
                            )
                          }
                          disabled={attachmentBusy(row.attachment.id)}
                          className="p-1.5 text-gray-500 hover:text-slate-800 hover:bg-gray-100 rounded-md"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => void removePending(row.clientId)}
                      disabled={removingClientId === row.clientId}
                      className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                      title={row.uploadStatus === 'uploading' ? 'Cancel upload' : 'Remove'}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              {canEditMeta && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    maxLength={500}
                    value={row.note}
                    onChange={(e) => updatePending(row.clientId, { note: e.target.value })}
                    placeholder="Note (optional)"
                    className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                  />
                  {showIncludeOnSend && (
                    <label className="flex items-center gap-2 text-sm text-gray-700 px-1">
                      <input
                        type="checkbox"
                        checked={row.include_on_send}
                        onChange={(e) =>
                          updatePending(row.clientId, { include_on_send: e.target.checked })
                        }
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      Include when sending
                    </label>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {visibleExisting.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Saved files</p>
          {existing.map((row) => {
            if (row.markedForDelete) return null;
            const { attachment } = row;
            return (
              <div
                key={attachment.id}
                className="rounded-lg border border-gray-200 bg-white p-3 space-y-2"
              >
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      handleOpen(attachment.download_url, attachment.filename, attachment.id)
                    }
                    disabled={attachmentBusy(attachment.id)}
                    className="flex items-start gap-2 min-w-0 flex-1 text-left rounded-md hover:bg-gray-50 disabled:opacity-60 transition-colors"
                    title="Open in new tab"
                  >
                    <FileText className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p
                        className="text-sm font-medium text-gray-900 truncate hover:underline"
                        title={attachment.filename}
                      >
                        {attachment.filename}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatAttachmentByteSize(attachment.byte_size)}
                        {attachment.uploaded_by?.name ? ` · ${attachment.uploaded_by.name}` : ''}
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() =>
                        handleOpen(attachment.download_url, attachment.filename, attachment.id)
                      }
                      disabled={attachmentBusy(attachment.id)}
                      className="p-1.5 text-gray-500 hover:text-slate-800 hover:bg-gray-100 rounded-md"
                      title="Open in new tab"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleDownload(attachment.download_url, attachment.filename, attachment.id)
                      }
                      disabled={attachmentBusy(attachment.id)}
                      className="p-1.5 text-gray-500 hover:text-slate-800 hover:bg-gray-100 rounded-md"
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => updateExisting(attachment.id, { markedForDelete: true })}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                {canEditMeta && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      maxLength={500}
                      value={row.note}
                      onChange={(e) => updateExisting(attachment.id, { note: e.target.value })}
                      placeholder="Note (optional)"
                      className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                    />
                    {showIncludeOnSend && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 px-1">
                        <input
                          type="checkbox"
                          checked={row.include_on_send}
                          onChange={(e) =>
                            updateExisting(attachment.id, { include_on_send: e.target.checked })
                          }
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        Include when sending
                      </label>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pending.length === 0 && visibleExisting.length === 0 && !canUpload && (
        <p className="text-sm text-gray-500">No attachments yet.</p>
      )}

      {pending.length === 0 && visibleExisting.length === 0 && canUpload && (
        <p className="text-sm text-gray-500">No files selected yet.</p>
      )}

      {existing.some((row) => row.markedForDelete) && (
        <p className="text-xs text-amber-700">
          {existing.filter((row) => row.markedForDelete).length} file(s) will be removed when you save.
        </p>
      )}
    </section>
  );
};

export default AttachmentsSection;
